import type { Definition } from 'typescript-json-schema';
import type { MetadataInstanceInterface } from '../../common/decoration/factory';
import type {
    v2,
    PageTypeV2,
    PageTypeV4,
    FileData,
    PropertyMessage,
    SyncRule,
    GenerateAppSchemaParameters,
    ChangeDefinition,
    ChangeContent,
    MessageParameters
} from '@sap/ux-specification-types';
import {
    ArtifactType,
    DefinitionName,
    SchemaTag,
    SchemaKeyName,
    ActionType,
    ControlType,
    PropertyMessageType,
    UIVOCABULARYALPHADOT,
    UIVOCABULARYDOT,
    LogSeverity
} from '@sap/ux-specification-types';
import type { AnnotationTerm, EntitySet, EntityType } from '@sap-ux/vocabularies-types';
import type {
    Chart,
    DataPoint,
    DataFieldAbstractTypes,
    LineItem,
    PresentationVariant,
    SelectionFields
} from '@sap-ux/vocabularies-types/vocabularies/UI';
import type * as Edm from '@sap-ux/vocabularies-types/Edm';
import type { EntityTypeAnnotations_UI } from '@sap-ux/vocabularies-types/vocabularies/UI_Edm';
import {
    addKeyToDefinition,
    getActionKey,
    DEFINITION_LINK_PREFIX,
    addDataTypeToDefinition,
    addCommonLineItemDefinitions,
    createAnnotationPath,
    determineDataType,
    getDataFieldDescription,
    getLabelForPropertyPath,
    getNextTargetDefinition,
    getSchemaKeyOfLineItemRecord,
    prepareRef,
    addEnumToSchema,
    parseChangeString
} from '../../common';
import { getDescriptionSrcURL, getReflectMetadata } from '../../common/decoration/decorators';
import { UIAnnotationTypes, UIAnnotationTerms } from '@sap-ux/vocabularies-types/vocabularies/UI';
import { sep } from 'path';
import { xml2json } from 'xml-js';
import { ActionTypeV2 } from '../types';
import { FacetBase } from '../../../../../types/src/common/types';
import { log } from '../../../extensionLogger';
import type { ExtensionLogger } from '../../..';
import { hasPropertyTypeError } from './manifestPropertyUtils';
import { getStableIdPartFromDataField } from './stableIdUtils';
import i18next from 'i18next';

/**
 * Common function for enhancing the LineItems definition of app schema by custom columns.
 *
 * @param appSchema - app-specific JSON schema
 * @param customColumnsDefinitionName - custom column definition name (plural)
 * @param customColumnDefinitionName - custom column definition name (singular)
 */
function addCustomColumnDefinition(
    appSchema: Definition,
    customColumnsDefinitionName: string,
    customColumnDefinitionName: string
): void {
    if (appSchema.definitions[DefinitionName.LineItems]) {
        (appSchema.definitions[DefinitionName.LineItems] as Definition).properties['custom'] = {
            $ref: `${DEFINITION_LINK_PREFIX}${customColumnsDefinitionName}`
        };
    }
    appSchema.definitions[customColumnsDefinitionName] = {
        type: 'array',
        items: {
            $ref: `${DEFINITION_LINK_PREFIX}${customColumnDefinitionName}`
        },
        description: 'Custom Columns'
    };
    appSchema.definitions[customColumnsDefinitionName][SchemaTag.isViewNode] = true;
    const extensionType = (appSchema.definitions[customColumnDefinitionName] as Definition).properties['extensionType'];
    if (extensionType) {
        extensionType[SchemaTag.artifactType] = ArtifactType.Manifest;
    }
}

/**
 * Prepares and returns id of action for further use and reference.
 *
 * @param actionType - type of action
 * @param lineItemProperties - properties of the line item collection: lineItemPath, lineItemRecord, lineItemId
 * @param lineItemProperties.lineItemPath - The path to the line item in the schema.
 * @param lineItemProperties.lineItemRecord - The record of the line item in the collection.
 * @param lineItemProperties.lineItemId - The identifier of the line item.
 * @param lineItemProperties.entityType - The entity type as part of the AVT ConvertedMetadata.
 * @returns {string} actionId
 */
function getActionId(
    actionType: ActionTypeV2,
    lineItemProperties: { lineItemPath: any; lineItemRecord: any; lineItemId: any; entityType: EntityType }
): string {
    let actionId: string;
    if (actionType === ActionTypeV2.ColumnIntentBased) {
        actionId = getSchemaKeyOfLineItemRecord(lineItemProperties.lineItemRecord);
    } else if (actionType === ActionTypeV2.ToolBarIntentBased || actionType === ActionTypeV2.FooterIntentBased) {
        actionId = prepareRef(
            `${lineItemProperties.lineItemRecord.SemanticObject}::${lineItemProperties.lineItemRecord.Action}`
        );
    } else if (
        actionType === ActionTypeV2.Column &&
        lineItemProperties.lineItemRecord.$Type === UIAnnotationTypes.DataFieldForAction
    ) {
        actionId = prepareRef(`DataFieldForAction:::sAction::${lineItemProperties.lineItemRecord.Action}`);
    } else {
        actionId = prepareRef(lineItemProperties.lineItemRecord.Action);
    }
    if (lineItemProperties.lineItemId && lineItemProperties.lineItemId !== 'LineItems') {
        return `${lineItemProperties.lineItemId}??${actionId}`;
    } else {
        return actionId;
    }
}

/**
 * Processes a LineItem record of type DataFieldForAction and DataFieldForIntentBasedNavigation during app schema generation.
 *
 * @param appSchema - the app specific schema that shall get enhanced
 * @param actions - actions definition in schema, parent object
 * @param lineItemProps - properties of the line item collection: lineItemPath, lineItemRecord, lineItemId
 * @param lineItemProps.lineItemPath - The path to the line item in the schema.
 * @param lineItemProps.lineItemRecord - The record of the line item in the collection.
 * @param lineItemProps.lineItemId - The identifier of the line item.
 * @param lineItemProps.entityType - the entity type as part of the AVT ConvertedMetadata
 * @param actionType - type of action
 * @param recordIndex - record index in the collection
 */
export function handleActionRecord(
    appSchema: Definition,
    actions: Definition,
    lineItemProps: { lineItemPath; lineItemRecord; lineItemId; entityType: EntityType },
    actionType: ActionTypeV2,
    recordIndex: number
): void {
    let actionReferenceType;
    const actionTemplate = lineItemProps.lineItemId === DefinitionName.LineItems ? '' : 'ObjectPage';
    const actionId = getActionId(actionType, lineItemProps);

    if (actionType === ActionTypeV2.Footer || actionType === ActionTypeV2.FooterIntentBased) {
        actionReferenceType = `Action`;
        actionType = ActionTypeV2.Footer;
    } else if (actionType === ActionTypeV2.Column || actionType === ActionTypeV2.ColumnIntentBased) {
        actionReferenceType = `TableColumnAction`;
    } else {
        actionReferenceType = actionTemplate !== '' ? `${actionTemplate}${actionType}` : `Action`;
        actionType = ActionTypeV2.ToolBar;
    }
    const actionDefinition = `${actionTemplate}${actionType}<${actionId}>`;
    const description = getDataFieldDescription(
        lineItemProps.lineItemRecord as DataFieldAbstractTypes,
        lineItemProps.entityType
    );
    actions.properties[actionId] = {
        $ref: DEFINITION_LINK_PREFIX + actionDefinition
    };
    actions.properties[actionId][SchemaTag.propertyIndex] = recordIndex;
    const { key, ns } = getActionKey(lineItemProps.lineItemRecord.Action, true);
    if (appSchema.definitions[actionReferenceType]) {
        appSchema.definitions[actionDefinition] = {
            ...JSON.parse(JSON.stringify(appSchema.definitions[actionReferenceType])),
            description,
            keys: [{ name: 'Action', value: key }],
            annotationPath: `${lineItemProps.lineItemPath}/${recordIndex}`
        };
        if (ns) {
            appSchema.definitions[actionDefinition][SchemaTag.target] = ns;
        }
    } else {
        appSchema.definitions[actionDefinition] = {
            type: 'object',
            properties: {},
            description,
            additionalProperties: false
        };
        appSchema.definitions[actionDefinition][SchemaTag.isViewNode] = true;
        appSchema.definitions[actionDefinition][SchemaTag.keys] = [{ name: SchemaKeyName.action, value: key }];
        if (ns) {
            appSchema.definitions[actionDefinition][SchemaTag.target] = ns;
        }
        appSchema.definitions[actionDefinition][SchemaTag.annotationPath] =
            `${lineItemProps.lineItemPath}/${recordIndex}`;
    }
}

/**
 * Returns the type of a column lineItem action.
 *
 * @param {boolean} isOP - true if we deal with the usage of the line item on an OP, false otherwise;
 * @param {object} lineItemRecord - actual record of the line item collection
 * @param actionTypes - array of action types to be filled
 */
function getColumnActionType(isOP: boolean, lineItemRecord, actionTypes: ActionTypeV2[]) {
    if (
        isOP &&
        lineItemRecord.Inline === true &&
        (lineItemRecord.$Type === UIAnnotationTypes.DataFieldForAction ||
            lineItemRecord.$Type === UIAnnotationTypes.DataFieldForIntentBasedNavigation)
    ) {
        actionTypes.push(ActionTypeV2.OPColumn);
    } else if (lineItemRecord.Inline === true && lineItemRecord.$Type === UIAnnotationTypes.DataFieldForAction) {
        actionTypes.push(ActionTypeV2.Column);
    } else if (
        lineItemRecord.Inline === true &&
        lineItemRecord.$Type === UIAnnotationTypes.DataFieldForIntentBasedNavigation
    ) {
        actionTypes.push(ActionTypeV2.ColumnIntentBased);
    }
}

/**
 * Returns the type of a lineItem action.
 *
 * @param {object} lineItemRecord - actual record of the line item collection
 * @param {boolean} isOP - true if we deal with the usage of the line item on an OP, false otherwise;
 * @returns {string[] | undefined } ActionTypeV2[] - action type(s) of the lineItem
 */
function getActionType(lineItemRecord: DataFieldAbstractTypes, isOP: boolean): ActionTypeV2[] | undefined {
    const actionTypes: ActionTypeV2[] = [];
    if (
        lineItemRecord.$Type !== UIAnnotationTypes.DataFieldForAction &&
        lineItemRecord.$Type !== UIAnnotationTypes.DataFieldForIntentBasedNavigation
    ) {
        return undefined;
    }
    if (
        lineItemRecord.Inline !== true &&
        lineItemRecord.Determining !== true &&
        lineItemRecord.$Type === UIAnnotationTypes.DataFieldForAction
    ) {
        actionTypes.push(ActionTypeV2.ToolBar);
    } else if (
        lineItemRecord.Inline !== true &&
        lineItemRecord.Determining !== true &&
        lineItemRecord.$Type === UIAnnotationTypes.DataFieldForIntentBasedNavigation
    ) {
        actionTypes.push(ActionTypeV2.ToolBarIntentBased);
    } else if (lineItemRecord.Determining === true && lineItemRecord.$Type === UIAnnotationTypes.DataFieldForAction) {
        actionTypes.push(ActionTypeV2.Footer);
    } else if (
        lineItemRecord.Determining === true &&
        lineItemRecord.$Type === UIAnnotationTypes.DataFieldForIntentBasedNavigation
    ) {
        actionTypes.push(ActionTypeV2.FooterIntentBased);
    }
    getColumnActionType(isOP, lineItemRecord, actionTypes);
    return actionTypes;
}

type LineItemProperties = {
    lineItemPath: string;
    lineItemRecord: DataFieldAbstractTypes;
    lineItemId: string;
    entityType: EntityType;
};

/**
 * Generates a generic column for the line item collection.
 *
 * @param lineItemProperties - Properties of the line item collection, including path, record, ID, and entity type.
 * @param appSchema - The app-specific JSON schema to be enhanced.
 * @param recordIndex - The index of the current record in the line item collection.
 */
function generateGenericColumn(lineItemProperties: LineItemProperties, appSchema: Definition, recordIndex: number) {
    const schemaKey = getSchemaKeyOfLineItemRecord(lineItemProperties.lineItemRecord);
    if (schemaKey) {
        const lineItemDefinition = appSchema.definitions[lineItemProperties.lineItemId] as Definition;
        lineItemDefinition.properties[schemaKey] = {
            $ref: DEFINITION_LINK_PREFIX + DefinitionName.TableColumn,
            description: getDataFieldDescription(lineItemProperties.lineItemRecord, lineItemProperties.entityType)
            // Custom property in schema - for object properties ordering purpose
        };
        lineItemDefinition.properties[schemaKey][SchemaTag.propertyIndex] = recordIndex;
        lineItemDefinition.properties[schemaKey][SchemaTag.annotationPath] =
            `${lineItemProperties.lineItemPath}/${recordIndex}`;
        addDataTypeToDefinition(lineItemDefinition.properties[schemaKey], lineItemProperties.lineItemRecord);
        addKeyToDefinition(lineItemProperties.lineItemRecord, lineItemDefinition.properties, schemaKey);
    }
}

/**
 * Handles any action type of a line item record.
 *
 * @param actionType - The type of action being processed.
 * @param appSchema - The app-specific JSON schema to be enhanced.
 * @param actions - The actions definition in the schema, parent object.
 * @param lineItemProperties - Properties of the line item collection, including path, record, ID, and entity type.
 * @param recordIndex - The index of the current record in the line item collection.
 * @param footerActions - The footer actions definition in the schema, if applicable.
 * @param lineItemId - The identifier of the line item, used to distinguish between different line item collections.
 */
function handleAnyAction(
    actionType: ActionTypeV2,
    appSchema: Definition,
    actions: object,
    lineItemProperties: LineItemProperties,
    recordIndex: number,
    footerActions: object,
    lineItemId: string
) {
    switch (actionType) {
        case ActionTypeV2.ToolBar:
        case ActionTypeV2.ToolBarIntentBased: {
            handleActionRecord(appSchema, actions, lineItemProperties, actionType, recordIndex);
            break;
        }
        case ActionTypeV2.Footer:
        case ActionTypeV2.FooterIntentBased: {
            if (footerActions) {
                handleActionRecord(appSchema, footerActions, lineItemProperties, actionType, recordIndex);
            }
            break;
        }
        case ActionTypeV2.Column:
        case ActionTypeV2.ColumnIntentBased: {
            handleActionRecord(
                appSchema,
                appSchema.definitions[lineItemId] as Definition,
                lineItemProperties,
                actionType,
                recordIndex
            );
            break;
        }
        case ActionTypeV2.OPColumn: {
            generateGenericColumn(lineItemProperties, appSchema, recordIndex);
        }
    }
}

/**
 *
 * @param annotationTerm "unqualified" annotation term that can be specified whithin a visualization (i.e. "LineItem" or "Chart")
 * @param visualization one visualization of a PresentationVariant
 * @returns empty string if the visualization does not match the annotationTerm. Otherwise the value of the visualization stripped by the reference to the ui vocabulary
 */
export function retrieveNameWithoutVocabularyFromVisualization(
    annotationTerm: FacetBase,
    visualization: Edm.AnnotationPath<Chart | DataPoint | LineItem>
): string {
    // check whether the value of the visualization specifies the fully qualified annotation term if yes, return the value without the vocabulary prefix, otherwise the empty string
    const visualizationValue = visualization.value;
    return visualizationValue.includes(UIVOCABULARYALPHADOT + annotationTerm) // replace includes with startsWith?
        ? visualizationValue.replace(UIVOCABULARYALPHADOT, '')
        : '';
}

/**
 * Adds the line item records to the app schema (for the list report or an object page section comprising a table).
 *
 * @param isObjectPage - indicates that the page is an object page
 * @param appSchema - the app specific schema that shall get enhanced
 * @param lineItemAnnotation - the UI.LineItem annotation, comprising all records
 * @param entityType - the entity type as part of the AVT ConvertedMetadata
 * @param lineItemId - optional: in case of OP the parameter must be passed to distinguish the OP tables;
 *                      in case of LR or ALP no ID is passed, 'LineItems' is taken then.
 * @param customColumnRef - schema reference to custom column definition.
 */
export function addLineItemsType(
    isObjectPage: boolean,
    appSchema: Definition,
    lineItemAnnotation: AnnotationTerm<LineItem>,
    entityType: EntityType,
    lineItemId?: string,
    customColumnRef?: string
): void {
    const isOP = !!lineItemId;
    // Add the correct lineItem definition
    lineItemId = lineItemId || DefinitionName.LineItems;

    const { actions, footerActions, lineItemPath } = addCommonLineItemDefinitions(
        isObjectPage,
        appSchema,
        lineItemAnnotation,
        entityType,
        lineItemId
    );

    if (lineItemAnnotation) {
        let recordIndex = 0;
        lineItemAnnotation.forEach((lineItemRecord) => {
            const lineItemProperties: LineItemProperties = { lineItemPath, lineItemRecord, lineItemId, entityType };
            const actionTypes = getActionType(lineItemRecord, isOP);
            if (actionTypes) {
                actionTypes.forEach((actionType) => {
                    handleAnyAction(
                        actionType,
                        appSchema,
                        actions,
                        lineItemProperties,
                        recordIndex,
                        footerActions,
                        lineItemId
                    );
                });
            } else {
                generateGenericColumn(lineItemProperties, appSchema, recordIndex);
            }
            recordIndex++;
        });
    }
    if (customColumnRef) {
        (appSchema.definitions[lineItemId] as Definition).properties['custom'] = {
            $ref: `${DEFINITION_LINK_PREFIX}${customColumnRef}`
        };
    }
}

/**
 * Adds enum entries of possible leading properties for 'leadingProperty' property in TableCustomColumn properties.
 *
 * @param appSchema - the app specific schema that shall get enhanced
 * @param entityType - the entity type as part of the AVT ConvertedMetadata
 * @param definitionName - definition name of CustomColumn definition
 */
export function addListReportLeadingProperties(
    appSchema: Definition,
    entityType: EntityType,
    definitionName: string
): void {
    // Leading Property
    const leadingPropertySchema = appSchema.definitions[definitionName]?.['properties']?.[
        'leadingProperty'
    ] as Definition;
    if (leadingPropertySchema) {
        const properties = [
            //  Entity properties
            ...(entityType?.entityProperties || []),
            // Navigation Properties
            ...(entityType?.navigationProperties || [])
        ];
        leadingPropertySchema['enum'] = properties?.reduce((result: string[], property) => {
            if (!result.includes(property.name)) {
                result.push(property.name);
            }
            return result;
        }, []);
    }
}

/**
 * Adds enum entries of posible tabKey properties for 'leadingProperty' property in TableCustomColumn properties.
 *
 * @param appSchema - the app specific schema that shall get enhanced
 * @param definitionName - definition name of CustomColumn definition
 * @param page - sapui5 page object
 * @param facetID -  The facet ID
 */
export function addTabkeyEnum(
    appSchema: Definition,
    definitionName: string,
    page?: v2.SapUiAppPageV2,
    facetID?: string
): void {
    const tabkeyPropertySchema = appSchema.definitions[definitionName]?.['properties']?.['tabKey'];
    if (!page) {
        if (facetID) {
            tabkeyPropertySchema.description =
                'In case of Object Page, the tabkey in manifest is equivalent to the facet ID.';
            tabkeyPropertySchema['enum'] = [facetID];
        }
        return;
    }
    const quickVariantsX = page.component?.settings?.quickVariantSelectionX?.['variants'];
    if (quickVariantsX) {
        const enumEntries = [];
        for (const index in quickVariantsX) {
            const key = quickVariantsX[index]?.key;
            if (key !== undefined && !enumEntries.includes(key)) {
                enumEntries.push(key);
            }
        }
        addEnumToSchema(enumEntries, tabkeyPropertySchema);
    }
}

/**
 * Adds enum entries of posible fragment entries.
 *
 * @param {object} appSchema The app specific schema that shall get enhanced.
 * @param {FileData[]} fragments Array of fragments.
 * @param {string[]} appId Application id.
 * @param {string} property Property name.
 * @param {string} definitionName Definition name of CustomColumn definition.
 */
export function addFragmentEnum(
    appSchema: Definition,
    fragments: FileData[],
    appId: string,
    property: string,
    definitionName: string
): void {
    if (!fragments || !appId || appId.startsWith('${')) {
        return;
    }
    const enumEntries = [];
    fragments.forEach((fragment) => {
        let relPath = fragment.dataSourceUri.split('webapp')[1];
        if (relPath && relPath.indexOf('.fragment.xml') > 0) {
            const fragmentJson = JSON.parse(xml2json(fragment.fileContent, { compact: true }))[
                'core:FragmentDefinition'
            ];
            if (
                (property === 'fragmentName' &&
                    (!fragmentJson ||
                        fragmentJson.Column ||
                        fragmentJson['table:Column'] ||
                        fragmentJson['table:AnalyticalColumn'])) ||
                (property === 'cellsFragmentName' &&
                    (!fragmentJson ||
                        (!fragmentJson.Column &&
                            !fragmentJson['table:Column'] &&
                            !fragmentJson['table:AnalyticalColumn'] &&
                            !fragmentJson['smartfilterbar:ControlConfiguration'])))
            ) {
                relPath = relPath.replace(new RegExp('\\' + sep, 'g'), '/');
                const fragmentName = appId + relPath.split('.fragment.xml')[0].replace(/\//g, '.');
                enumEntries.push(fragmentName);
            }
        }
    });
    addEnumToSchema(enumEntries, appSchema.definitions[definitionName]?.['properties']?.[property]);
}

/**
 * Adds the custom columns to the line item records of the app schema (for the list report comprising a table).
 *
 * @param appSchema The app specific schema that shall get enhanced.
 * @param entityType The entity type as part of the AVT ConvertedMetadata.
 * @param page Sapui5 page object.
 * @param manifest Application manifest.
 * @param [fragments] Array of fragments.
 * @param [customColumnsDefinitionName] Definition name of Custom Columns definition.
 * @param [customColumnDefinitionName] Definition name of Custom Column definition.
 * @param facetID -  Facet ID
 */
export function addTableColumnExtensions(
    appSchema: object,
    entityType: EntityType,
    page: v2.SapUiAppPageV2,
    manifest: object,
    fragments?: FileData[],
    customColumnsDefinitionName: string = DefinitionName.CustomColumns,
    customColumnDefinitionName: string = DefinitionName.CustomColumn,
    facetID?: string
): void {
    addCustomColumnDefinition(appSchema, customColumnsDefinitionName, customColumnDefinitionName);

    // Leading Property
    addListReportLeadingProperties(appSchema, entityType, customColumnDefinitionName);
    // Tab key property
    addTabkeyEnum(appSchema, customColumnDefinitionName, page, facetID);
    // Fragment property
    const appId = manifest && manifest['sap.app'] && manifest['sap.app']['id'];
    addFragmentEnum(appSchema, fragments, appId, 'fragmentName', customColumnDefinitionName);
    //tools-suite does not distinguish by section ID, looks at the generic entry
    addFragmentEnum(appSchema, fragments, appId, 'fragmentName', DefinitionName.CustomColumn);
    addFragmentEnum(appSchema, fragments, appId, 'cellsFragmentName', customColumnDefinitionName);
    addFragmentEnum(appSchema, fragments, appId, 'cellsFragmentName', DefinitionName.CustomColumn);
}

/**
 * Processes an anyOf definition of the generic schema.
 *
 * @param propertyDefinition - current definition in the generic schema
 * @param schema - the app-specific schema
 * @param propertyKey - key of the single property
 * @param factory - factory for creating metadata instances
 * @param pageType - page type
 * @param processedRefs - array of already processed refs which used to avoid cycling recursion
 */
function processAnyOf(
    propertyDefinition: any,
    schema: object,
    propertyKey: string,
    factory: MetadataInstanceInterface,
    pageType: PageTypeV2 | PageTypeV4,
    processedRefs: string[]
) {
    propertyDefinition['anyOf'].forEach((element) => {
        const nextDefinition = getNextTargetDefinition(schema, '', {}, element, propertyKey, factory, pageType);
        if (nextDefinition) {
            processMetadata(
                schema,
                nextDefinition.targetDefinition,
                nextDefinition.configObject,
                pageType,
                factory,
                processedRefs,
                element.$ref
            );
        }
    });
}

/**
 * Processes an object reference of the generic schema.
 *
 * @param schema - The JSON schema to be processed.
 * @param propertyDefinition - current definition in the generic schema
 * @param propertyKey - key of the single property
 * @param factory - factory for creating metadata instances
 * @param pageType - page type
 * @param processedRefs - array of already processed refs which used to avoid cycling recursion
 */
function processObject(
    schema: object,
    propertyDefinition: any,
    propertyKey: string,
    factory: MetadataInstanceInterface,
    pageType: PageTypeV2 | PageTypeV4,
    processedRefs: string[]
) {
    const nextDefinition = getNextTargetDefinition(schema, '', {}, propertyDefinition, propertyKey, factory, pageType);
    if (nextDefinition) {
        processMetadata(
            schema,
            nextDefinition.targetDefinition,
            nextDefinition.configObject,
            pageType,
            factory,
            processedRefs,
            propertyDefinition.$ref
        );
    }
}

/**
 * Determines the artifact type of a property and adds it to the property definition in schema.
 *
 * @param syncRule - sync rule of the current property
 * @param propertyDefinition - current definition in the generic schema
 */
function addArtifactType(syncRule: SyncRule, propertyDefinition: any) {
    if (syncRule.manifest !== undefined) {
        propertyDefinition.artifactType = ArtifactType.Manifest;
    } else if (syncRule.flex !== undefined) {
        propertyDefinition.artifactType = ArtifactType.FlexChange;
        //Add control type
        if (syncRule.flex.controlType) {
            propertyDefinition.controlType = syncRule.flex.controlType();
        }
    } else if (syncRule.processingRuleAdapter === undefined) {
        // artifact type 'annotation' is actually useless in V2. For compatibility we leave it in for legacy cases, until new logic has been established everywhere.
        propertyDefinition.artifactType = ArtifactType.Annotation;
    }
}

/**
 * After the generation of a generic schema, apply post-generation rule to a single property.
 *
 * @param metaDataDefinition - The metadata definition object containing property configurations and rules.
 * @param schema - given app schema, to be adapted
 * @param pageType - page type
 * @param factory - factory for creating metadata instances
 * @param propertyDefinition - definition of the single property
 * @param propertyKey - key of the single property
 * @param schemaDefinition - the current entry point or definition in the app schema
 * @param processedRefs - array of already processed refs which used to avoid cycling recursion
 */
function processSingleProperty(
    metaDataDefinition: { [key: string]: any },
    schema: object,
    pageType: PageTypeV2 | PageTypeV4,
    factory: MetadataInstanceInterface,
    propertyDefinition: any,
    propertyKey: string,
    schemaDefinition: object,
    processedRefs: string[]
): void {
    if (metaDataDefinition) {
        //get sync rule from reflect metadata
        const syncRule = getReflectMetadata(metaDataDefinition, propertyKey);
        if (syncRule) {
            if (typeof syncRule.generate === 'function') {
                syncRule.generate(schema, schemaDefinition, propertyKey);
                propertyDefinition = schemaDefinition['properties'][propertyKey];
            }
            //Add artifact type
            addArtifactType(syncRule, propertyDefinition);
        }
        //get getDescriptionSrcURL from reflect metadata
        const descriptionSrcURL = getDescriptionSrcURL(metaDataDefinition, propertyKey);
        if (descriptionSrcURL) {
            propertyDefinition[SchemaTag.descriptionSrcURL] = descriptionSrcURL;
        }

        if (propertyDefinition.type === 'object' || propertyDefinition.$ref) {
            processObject(schema, propertyDefinition, propertyKey, factory, pageType, processedRefs);
        } else if (propertyDefinition['anyOf']) {
            processAnyOf(propertyDefinition, schema, propertyKey, factory, pageType, processedRefs);
        }
    }
}

/**
 * After the generation of a generic schema, apply rules to a list of properties of the given schema section.
 *
 * @param schema - given app schema, to be adapted
 * @param schemaDefinition - the current entry point or definition in the app schema
 * @param metaDataDefinition - the current entry point or definition in the metadata
 * @param propertiesSection - the section of the schema containing properties to process (e.g., 'properties', 'additionalProperties', or 'patternProperties')
 * @param pageType - page type
 * @param factory - factory for creating metadata instances
 * @param processedRefs - array of already processed refs which used to avoid cycling recursion
 */
function processProperties(
    schema: object,
    schemaDefinition: object,
    metaDataDefinition: { [key: string]: any },
    propertiesSection: string,
    pageType: PageTypeV2 | PageTypeV4,
    factory: MetadataInstanceInterface,
    processedRefs: string[]
) {
    if (schemaDefinition[propertiesSection]) {
        for (const propertyKey in schemaDefinition[propertiesSection]) {
            if (propertyKey === 'anyOf') {
                processAnyOf(
                    schemaDefinition[propertiesSection],
                    schema,
                    propertyKey,
                    factory,
                    pageType,
                    processedRefs
                );
            } else {
                const propertyDefinition = schemaDefinition[propertiesSection][propertyKey];
                if (propertyDefinition['anyOf']) {
                    processAnyOf(propertyDefinition, schema, propertyKey, factory, pageType, processedRefs);
                } else if (propertyKey === '$ref') {
                    processObject(
                        schema,
                        schemaDefinition[propertiesSection],
                        propertyKey,
                        factory,
                        pageType,
                        processedRefs
                    );
                } else {
                    processSingleProperty(
                        metaDataDefinition,
                        schema,
                        pageType,
                        factory,
                        propertyDefinition,
                        propertyKey,
                        schemaDefinition,
                        processedRefs
                    );
                }
            }
        }
    }
}

/**
 * Apply post-processing rules as specified in form of "generate" at the syncRule.
 *
 * @param schema - given app schema, to be adapted
 * @param schemaDefinition - the current entry point or definition in the app schema
 * @param metaDataDefinition - the current entry point or definition in the metadata
 * @param pageType - page type
 * @param factory - factory for creating metadata instances
 * @param processedRefs - array of already processed refs which used to avoid cycling recursion
 * @param ref - currently processed schema definition reference name
 */
export function processMetadata(
    schema: object,
    schemaDefinition: object,
    metaDataDefinition: { [key: string]: any },
    pageType: PageTypeV2 | PageTypeV4,
    factory: MetadataInstanceInterface,
    processedRefs: string[],
    ref?: string
): void {
    if (ref) {
        if (processedRefs.includes(ref)) {
            return;
        }
        processedRefs.push(ref);
    }
    processProperties(schema, schemaDefinition, metaDataDefinition, 'properties', pageType, factory, processedRefs);
    processProperties(
        schema,
        schemaDefinition,
        metaDataDefinition,
        'additionalProperties',
        pageType,
        factory,
        processedRefs
    );
    processProperties(
        schema,
        schemaDefinition,
        metaDataDefinition,
        'patternProperties',
        pageType,
        factory,
        processedRefs
    );
}

/**
 * Method updates passed 'enum' array by removing passed matching entries.
 *
 * @param {Array<unknown>} values Array of values.
 * @param {Array<unknown>} valuesToRemove Array of values to remove.
 */
export function removeElementsFromEnum(values: Array<unknown>, valuesToRemove: Array<unknown>): void {
    for (const value of valuesToRemove) {
        const index = values.indexOf(value);
        if (index !== -1) {
            // Value exists - remove it
            values.splice(index, 1);
        }
    }
}

/**
 * Adds the selection fields to the app schema, as properties of the Filterbar.
 *
 * @param appSchema - the app specific schema that shall get enhanced
 * @param selectionFieldsAnnotation - the UI.SelectionField annotation
 * @param entityType - the entity type as part of the AVT ConvertedMetadata
 */
export function addSelectionFields(
    appSchema: Definition,
    selectionFieldsAnnotation: AnnotationTerm<SelectionFields>,
    entityType: EntityType
): void {
    appSchema.definitions.SelectionFields = {
        ...(appSchema.definitions.SelectionFields as object),
        properties: {},
        additionalProperties: false
    };
    const selectionFieldsDefinition = appSchema.definitions.SelectionFields;
    const annotationPath = createAnnotationPath(entityType?.fullyQualifiedName, UIAnnotationTerms.SelectionFields);
    if (annotationPath) {
        selectionFieldsDefinition[SchemaTag.annotationPath] = annotationPath;
    }
    if (!selectionFieldsAnnotation) {
        return;
    }
    let i = 0;
    selectionFieldsAnnotation.forEach((selectionField) => {
        let description = selectionField.value;
        const fieldDefinition = `SelectionField<${prepareRef(selectionField.value)}>`;
        const dataType = determineDataType(selectionField);
        if (selectionField.type === 'PropertyPath') {
            description = getLabelForPropertyPath(selectionField, entityType);
        }
        selectionFieldsDefinition.properties[selectionField.value] = {
            $ref: DEFINITION_LINK_PREFIX + fieldDefinition
        };
        selectionFieldsDefinition.properties[selectionField.value][SchemaTag.propertyIndex] = i;
        appSchema.definitions[fieldDefinition] = {
            type: 'object',
            description,
            additionalProperties: false
        };
        appSchema.definitions[fieldDefinition][SchemaTag.isViewNode] = true;
        appSchema.definitions[fieldDefinition][SchemaTag.annotationType] = selectionField?.type;
        appSchema.definitions[fieldDefinition][SchemaTag.dataType] = dataType;
        appSchema.definitions[fieldDefinition][SchemaTag.keys] = [
            { name: SchemaKeyName.value, value: `${selectionField.value}` }
        ];
        appSchema.definitions[fieldDefinition][SchemaTag.annotationPath] = `${
            selectionFieldsDefinition[SchemaTag.annotationPath]
        }/${i}`;

        if (['Date', 'DateTime', 'DateTimeOffset'].includes(dataType)) {
            appSchema.definitions[fieldDefinition]['$ref'] = `${DEFINITION_LINK_PREFIX}SelectionFieldDateSettings`;
        } else {
            appSchema.definitions[fieldDefinition]['properties'] = {};
        }
        i++;
    });
}

/**
 * Add entityProperties to passed a passed enum entry.
 *
 * @param {string[]} appSchemaEnum - Enum to be enhanced
 * @param {EntityType} entityType - Given entity type for which entries shall get generated
 */
export function addEntityPropertiesToEnum(appSchemaEnum: string[], entityType: EntityType): void {
    for (const key of entityType.entityProperties) {
        appSchemaEnum.push(key.name);
    }
}

/**
 * Checks if an entity supports draft capabilities.
 *
 * @param {EntitySet} entitySet Entity Set
 * @returns true if the entity supports draft
 */
export function isDraftEnabled(entitySet: EntitySet): boolean {
    return !!entitySet.annotations?.Common?.DraftRoot || !!entitySet.annotations?.Common?.DraftNode;
}

/**
 * Function to determine if the entity is deletable.
 *
 * @param {EntitySet} entitySet - current entity set
 * returns {boolean} return true if the Entity is deletable
 */

/**
 * Checks if an entity supports draft capabilities.
 *
 * @param entitySet - The entity set to check for draft capabilities.
 * @returns {boolean} True if the entity supports draft capabilities, otherwise false.
 */
export function isEntityDeletable(entitySet: EntitySet): boolean {
    return (entitySet.annotations?.Capabilities?.DeleteRestrictions?.Deletable as unknown as boolean) !== false;
}

/**
 * Function to determine if the entity is updatable.
 *
 * @param {EntitySet} entitySet - current entity set
 * @returns returns true if the Entity is updatable
 */
export function isEntityUpdatable(entitySet: EntitySet): boolean {
    return (entitySet.annotations?.Capabilities?.UpdateRestrictions?.Updatable as unknown as boolean) !== false;
}

/**
 * Adds standard header actions to schema (LR, ALP or OP).
 *
 * @param {Definition} appSchema - app-specific JSON schema
 * @param {boolean} isOP - flag to add to either LR/ALP or OP definition
 * @param {Definition} actions - optional standard actions definition
 */
export function addStandardHeaderActions(appSchema: Definition, isOP = false, actions?: Definition): void {
    // add standard header actions definitions
    let headerActionsKey = DefinitionName.HeaderActionsStandard;
    let actionDefinition = DefinitionName.HeaderActionStandard;
    if (isOP) {
        headerActionsKey = DefinitionName.ObjectPageHeaderActions;
        actionDefinition = DefinitionName.ObjectPageHeaderActionStandard;
    }
    const headerActions = appSchema.definitions[headerActionsKey] as Definition;
    headerActions[SchemaTag.isViewNode] = true;
    headerActions.description = 'Actions';
    const standardActions = actions || headerActions;
    Object.entries(standardActions.properties).forEach(([name, definition]) => {
        const actionRef = definition['$ref'].split(DEFINITION_LINK_PREFIX)[1];
        const actionKey = `${actionDefinition}<${actionRef}>`;
        headerActions.properties[name] = { $ref: `${DEFINITION_LINK_PREFIX}${actionKey}` };
        appSchema.definitions[actionKey] = appSchema.definitions[actionRef];
        appSchema.definitions[actionRef][SchemaTag.actionType] = ActionType.Standard;
    });
}

/**
 * Adds standard footer actions to schema of Object Page.
 *
 * @param {Definition} appSchema - app-specific JSON schema
 * @param {Definition} actions - optional standard actions definition
 */
export function addStandardFooterActionsOP(appSchema: Definition, actions: Definition): void {
    const standardActions = appSchema.definitions[DefinitionName.ObjectPageFooterActions] as Definition;
    for (const action in standardActions.properties) {
        const actionRef = standardActions.properties[action]['$ref'].split(DEFINITION_LINK_PREFIX)[1];
        const actionKey = `${DefinitionName.ObjectPageFooterActionStandard}<${actionRef}>`;
        actions.properties[action] = { $ref: `${DEFINITION_LINK_PREFIX}${actionKey}` };
        appSchema.definitions[actionKey] = appSchema.definitions[actionRef];
        appSchema.definitions[actionRef][SchemaTag.actionType] = ActionType.Standard;
    }
}

/**
 * Extract entity type and ui annotations for a entity set and log an error in case that either of these objects does not exist.
 *
 * @param {GenerateAppSchemaParameters} generateParameters list of API input parameters
 * @returns the entity type and the ui annotations for the given entity set
 */
export function getUIAnnotationForEntitySet(generateParameters: GenerateAppSchemaParameters): {
    entityType?: Edm.EntityType;
    uIAnnotations?: EntityTypeAnnotations_UI;
} {
    const entityType = generateParameters.entitySet?.entityType;
    const uIAnnotations = entityType?.annotations?.UI;
    if (typeof uIAnnotations !== 'object' || Object.keys(uIAnnotations).length === 0) {
        const message = entityType
            ? i18next.t('NOUIANNOTATIONS', { entityType: entityType.name })
            : i18next.t('NOENTITYTYPE4SET', { entitySetName: generateParameters.entitySet?.name });
        log(generateParameters.logger, {
            severity: LogSeverity.Error,
            message: message
        });
    }
    return { entityType, uIAnnotations };
}

/**
 * Helper function for getLineItemAnnotation which determines the presentation variant to be used. The parameters of both functions have identical meaning.
 *
 * @param uIAnnotations - The UI annotations for the entity type.
 * @param annotationPath - Optional annotation path to identify the specific presentation variant.
 * @param logger - Optional logger instance for logging warnings or errors.
 * @returns The determined PresentationVariant or undefined if none is found.
 */
function getPresentationVariantForLineItem(
    uIAnnotations: EntityTypeAnnotations_UI,
    annotationPath?: string,
    logger?: ExtensionLogger
): PresentationVariant | undefined {
    let presentationVariant: PresentationVariant | undefined; // the presentation variant to be used to determine the LineItem
    // Note: annotationPath (if it exists) contains the fully qualified annotation name (starting with UIVOCABULARYDOT) which would be a member of enum UIAnnotationTerms.
    // Inside uiAnnotations the unqualified annotation names are used as keys. They can be found in enum FacetBase or obtained from the terms above by removing UIVOCABULARYDOT.
    if (annotationPath) {
        const isSPV = annotationPath.includes(UIAnnotationTerms.SelectionPresentationVariant);
        const isPV = !isSPV && annotationPath.includes(UIAnnotationTerms.PresentationVariant);
        if (isSPV || isPV) {
            const accessor = annotationPath.replace(UIVOCABULARYDOT, '');
            const uiAnnotation = uIAnnotations[accessor]; // the ui annotation specified by the annotationPath if it exists
            if (uiAnnotation) {
                if (isPV) {
                    presentationVariant = uiAnnotation;
                } else {
                    // SPV case
                    presentationVariant = uiAnnotation[FacetBase.PresentationVariant];
                    if (
                        presentationVariant &&
                        presentationVariant.fullyQualifiedName.includes(FacetBase.SelectionPresentationVariant)
                    ) {
                        presentationVariant = uIAnnotations[FacetBase.PresentationVariant]; // inline presentation variants are ignored, use standard presentation variant instead
                    }
                }
            } else {
                // annotationPath is pointing to a selection presentation variant or a presentation variant but the corresponding annotation does not exist
                log(logger, {
                    severity: LogSeverity.Warning,
                    message: i18next.t('NOANNOTATIONFORPATH', { annotationPath: annotationPath })
                });
                // If the annotationPath has specified a non-existing selection presentation variant fall back to the standard presentation variant
                if (isSPV) {
                    presentationVariant = uIAnnotations[FacetBase.PresentationVariant];
                }
            }
        } else {
            // annotationPath exists but does not point to a selection presentation variant or presentation variant (should point to a selection variant then)
            presentationVariant = uIAnnotations[FacetBase.PresentationVariant]; // use standard presentation variant in this case
        }
    } else {
        presentationVariant = uIAnnotations[FacetBase.PresentationVariant];
    }
    return presentationVariant;
}

/**
 * Determine the LineItemAnnotation to be used for the LR.
 * Reproduces logic from Fiori Elements LR implementation (function createWorkingContext in LR specific AnnotationHelper).
 *
 * @param uIAnnotations - The UI annotations for the entity type.
 * @param settings - Optional settings object, typically a piece of the manifest, containing an optional annotationPath property.
 * @param logger - Optional logger instance for logging warnings or errors.
 * @returns The determined LineItem annotation or undefined if none is found.
 */
export function getLineItemAnnotation(
    uIAnnotations: EntityTypeAnnotations_UI,
    settings?: unknown, // an object (actually a piece of the manifest) possessing an optional property annotationPath which would define the path to the PresentationVariant
    logger?: ExtensionLogger // used in case an error/warning is detected while determining the result
): LineItem | undefined {
    if (hasPropertyTypeError(settings, 'annotationPath', logger)) {
        return undefined; // return undefined in case there is an error in the settings
    }
    const presentationVariant = getPresentationVariantForLineItem(
        uIAnnotations,
        (settings as object)?.['annotationPath'] as unknown as string | undefined,
        logger
    ); // the presentation variant to be used to determine the LineItem (if there is one)

    // If a PV could be determined LineItem should be defined as a visualization of the PV.
    const visualizations: Edm.AnnotationPath<Chart | DataPoint | LineItem>[] =
        presentationVariant?.Visualizations || [];
    let lineItemName: string = ''; // the name that will be used to retrieve the LineItem from uiAnnotations
    visualizations.some(function (visualization) {
        lineItemName = retrieveNameWithoutVocabularyFromVisualization(FacetBase.LineItem, visualization);
        return lineItemName; // end loop as soon as name of LineItem has been identified
    });
    // If the above logic did not identify a name for the LineItem then still the corresponding annotation (but without qualifier) will be used
    lineItemName = lineItemName || FacetBase.LineItem;
    const lineItem = uIAnnotations[lineItemName] as LineItem | undefined;
    if (!lineItem) {
        log(logger, {
            severity: LogSeverity.Warning,
            message: i18next.t('NOLINEITEMFORPATH', { annotationPath: lineItemName })
        });
    }
    return lineItem;
}

/**
 * Possible placements for a datafield in the ui.
 */
export enum DataFieldTarget {
    Column = 'Column',
    Toolbar = 'Toolbar', // This value represents the toolbar of a table or chart. Note that the ui5 control sap.m.Toolbar may also be used for other purposes (e.g. the footer of a page)
    Footer = 'Footer'
}

/**
 * An instance of this type collects information about one data field within a line item.
 *
 * @property target - where the data field is placed in the ui
 * @property stableIdPart - result of getStableIdPartFromDataField() for this data field, resp. '' if that function returned undefined (only happening if the service is the datafield is not modelled correctly)
 * @property isAction - true if the data field is of type DataFieldForAction or DataFieldForIntentBasedNavigation
 * @property description - the text of the data field (if any)
 * @property dataField - the actual data field from the ui annotations
 * @property message - possibly a message indicating an issue with this data field
 */
export type DataFieldInfo = {
    target: DataFieldTarget;
    stableIdPart: string;
    isAction: boolean;
    description: string;
    dataField: DataFieldAbstractTypes;
    message?: PropertyMessage;
};

/**
 * An instance of this type collects information about the line item configured for a table.
 *
 * @property name - the name of the line item annotation (including qualifier if any)
 * @property dataFieldInfos - information about each data field within the line item
 */
export type LineItemInfo = {
    name: string;
    dataFieldInfos: DataFieldInfo[];
};

/**
 * Determines the target placement of an action based on its type.
 *
 * @param actionType - The type of the action
 * @returns The target placement for the action, or undefined if no target can be determined
 */
function getTargetFromActionType(actionType: ActionTypeV2): DataFieldTarget | undefined {
    switch (actionType) {
        case ActionTypeV2.ToolBar:
        case ActionTypeV2.ToolBarIntentBased: {
            return DataFieldTarget.Toolbar;
        }
        case ActionTypeV2.Footer:
        case ActionTypeV2.FooterIntentBased: {
            return DataFieldTarget.Footer;
        }
        case ActionTypeV2.Column:
        case ActionTypeV2.ColumnIntentBased: {
            return DataFieldTarget.Column;
        }
        case ActionTypeV2.OPColumn: {
            return undefined; // TODO: Revisit when this code should also be used for OP
        }
    }
}

/**
 * Collects all relevant information about a given line item.
 *
 * @param lineItemName - the name of the line item annotation (including qualifier if any)
 * @param lineItemDefinition - the definition of the line item
 * @param entityType - the entity type the line item belongs to
 * @param tableType - the table type the line item is used for
 * @param generateParameters - the generation parameters of the process the information is collected for. Used for logging.
 * @param isOP - whether the line item is for an object page
 * @returns an instance of LineItemInfo containing the collected information
 */
export function getLineItemInfo(
    lineItemName: string,
    lineItemDefinition: AnnotationTerm<LineItem>,
    entityType: Edm.EntityType,
    tableType: string,
    generateParameters: GenerateAppSchemaParameters,
    isOP: boolean
): LineItemInfo {
    const dataFieldInfos: DataFieldInfo[] = [];
    lineItemDefinition.forEach((lineItemRecord: DataFieldAbstractTypes) => {
        const isDataField = lineItemRecord.$Type === UIAnnotationTypes.DataField;
        const stableIdPart = getStableIdPartFromDataField(lineItemRecord, generateParameters) || '';
        const description = getDataFieldDescription(lineItemRecord, entityType);
        const actionTypes = isDataField ? undefined : getActionType(lineItemRecord, isOP);
        if (actionTypes) {
            const message: PropertyMessage = lineItemRecord.Label
                ? undefined
                : {
                      type: PropertyMessageType.Warning,
                      text: i18next.t('NOLABELFORBUTTON')
                  };
            actionTypes.forEach((actionType) => {
                const target = getTargetFromActionType(actionType); // Note that actionType has been produced by getActionType, so undefined can be excluded
                const dataFieldInfo: DataFieldInfo = {
                    isAction: true,
                    target,
                    stableIdPart,
                    description,
                    dataField: lineItemRecord
                };
                if (message) {
                    dataFieldInfo.message = message;
                }
                dataFieldInfos.push(dataFieldInfo);
            });
        } else {
            const dataFieldInfo: DataFieldInfo = {
                isAction: false,
                target: DataFieldTarget.Column,
                stableIdPart,
                description,
                dataField: lineItemRecord
            };
            dataFieldInfos.push(dataFieldInfo);
        }
    });
    return {
        name: lineItemName,
        dataFieldInfos: dataFieldInfos
    };
}

/**
 * This function provides the information how to deal with the outdated useExportToExcel property for tables in the app schema, based on the existing flex changes for the current page.
 * Thereby, the table properties enableExport and useExportToExcel are checked.
 * We assume that the first of those is preferred and the second one is only supported for compatibility.
 *
 * @param flex - the flex changes applicable to the current page
 * @param smartTableControlId - if this parameter is truthy only flex changes for smart tables with this local id will be considered.
 * Otherwise all flex changes for any smart table will be considered.
 * Note that anyway only flex changes for the two properties mentioned above are relevant.
 * @returns the name of the unfavored property (useExportToExcel) and possibly a message indicating how to deal with it in the app schema (e.g. whether to simply hide it or whether there is an inconsistency that should be resolved by the user).
 */
export function getExportTablesPropertiesInfo(
    flex: string[],
    smartTableControlId?: string
): { unfavoredName: string; message: MessageParameters | undefined } {
    const preferredName = 'enableExport';
    const unfavoredName = 'useExportToExcel';
    let preferredChange: ChangeContent | undefined;
    let unfavoredChange: ChangeContent | undefined;
    flex.forEach(function (flexContent: string) {
        const change = parseChangeString(flexContent) as ChangeDefinition;
        if (
            change.controlType === ControlType.SmartTable &&
            (!smartTableControlId || change.controlId.split('--')[1] == smartTableControlId)
        ) {
            if (change.content.property === preferredName) {
                preferredChange = change.content;
            } else if (change.content.property === unfavoredName) {
                unfavoredChange = change.content;
            }
        }
    });
    let message: MessageParameters | undefined;

    if (unfavoredChange) {
        // A change of the unfavored property exists. => Inform user that he should better use the preferred property.
        // The exact message depends on the situation.
        if (
            preferredChange?.newValue === unfavoredChange.newValue &&
            preferredChange?.newBinding === unfavoredChange.newBinding
        ) {
            message = {
                text: i18next.t('USE_ENABLE_EXPORT_ONLY'),
                deletable: true,
                type: PropertyMessageType.Info
            }; // Change for unfavored property is simply superfluous, since there is a redundant change for the preferred property.
        } else if (preferredChange) {
            message = {
                text: i18next.t('ENABLE_EXPORT_INCONSISTENT'),
                deletable: true,
                type: PropertyMessageType.Error
            }; // Changes for the two properties exist and are inconsistent -> Seems to be a misconfiguration
        } else {
            message = {
                text: i18next.t('EXPORT_TO_EXCEL_OUTDATED'),
                type: PropertyMessageType.Info,
                quickFix: {
                    text: i18next.t('TRANSFER_VALUE'),
                    values: { [preferredName]: unfavoredChange.newValue ?? unfavoredChange.newBinding },
                    remove: true
                }
            }; // Only change for the unfavored property exists -> Provide option to transfer it to the preferred property
        }
    }
    return { unfavoredName, message: message };
}

/**
 * An instance of this type can be used to modify a section of a schema (e.g. in a post processing step for the app specific schema generation).
 *
 * @param definition - the schema section to be modified
 */
export type DefinitionAdapter = (definition: Definition) => void;

/**
 * This function provides a DefinitionAdapter that will modify sections of a schema that represent a table.
 * Thereby, the table properties enableExport and useExportToExcel are checked.
 * We assume that the first of those is preferred and the second one is only supported for compatibility.
 * Note that this is valid starting with UI5 release 1.142. Hence, this function should not be used for lower releases.
 *
 * @param flex - the flex changes applicable to the current page
 * @param smartTableControlId - if this parameter is truthy only flex changes for smart tables with this local id will be considered.
 * Otherwise all flex changes for any smart table will be considered.
 * Note that anyway only flex changes for the two properties mentioned above are relevant.
 * @returns a DefinitionAdapter that should be applied to all table sections that correspond to the specified SmartTable.
 */
export function getExportTablesPropertiesAdapter(flex: string[], smartTableControlId?: string): DefinitionAdapter {
    const { unfavoredName, message } = getExportTablesPropertiesInfo(flex, smartTableControlId);
    // If there is a message provide a function that sets it. Otherwise provide a function that hides the unfavored property.
    const schemaTagToSet = message ? SchemaTag.messages : SchemaTag.hidden;
    const valueToSet = message ? [message] : true;
    return function (tableDefinition: Definition): void {
        const elementToAdapt = tableDefinition?.properties?.[unfavoredName];
        if (elementToAdapt) {
            elementToAdapt[schemaTagToSet] = valueToSet;
        }
    };
}
